import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { usePurchaseConsigneeSellerAccountId } from 'src/contexts/PurchaseConsigneeContext';
import { PosMultiSelectProps } from 'src/core/POS/PosMultiSelect';
import { PosSelectProps } from 'src/core/POS/PosSelect';
import { useFavoriteVendorIds } from 'src/hooks/useFavoriteVendorIds';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { PurchaseClient, PurchaseVendor } from 'src/WebApiController';

export type UsePurchaseVendorSelectorOptions = {
  disabled?: boolean;
  additionalOptions?: Record<string, string>;
  additionalOptionsPosition?: 'top' | 'bottom';
  placeholderText?: string | ContentId;
};

export type PurchaseVendorSelectorProps = Omit<
  PosSelectProps,
  'valueOptionsContent'
> &
  UsePurchaseVendorSelectorOptions;

export type PurchaseVendorMultiSelectorProps = Omit<
  PosMultiSelectProps,
  'valueOptionsContent'
> &
  UsePurchaseVendorSelectorOptions;

export const usePurchaseVendorSelector = ({
  disabled,
  additionalOptions,
  additionalOptionsPosition = 'bottom',
  placeholderText,
}: UsePurchaseVendorSelectorOptions): {
  query: UseQueryResult<PurchaseVendor[] | null>;
  availableVendors: Record<string, PurchaseVendor>;
  posSelectProps: Pick<
    PosSelectProps,
    | 'placeholderText'
    | 'valueOptionsContent'
    | 'sortMode'
    | 'searchable'
    | 'sortFn'
    | 'loading'
  >;
} => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();

  const { favoriteVendorIds } = useFavoriteVendorIds();
  const consigneeSellerAccountId = usePurchaseConsigneeSellerAccountId();

  const shouldQuery =
    !disabled && activeAccountWebClientConfig.activeAccountId != null;
  const query = useQuery({
    queryKey: [
      'getAccessibleVendors',
      activeAccountWebClientConfig.activeAccountId,
      consigneeSellerAccountId,
    ],
    queryFn: () => {
      if (!shouldQuery) {
        return null;
      }
      return tryInvokeApi(
        () => {
          return new PurchaseClient(
            activeAccountWebClientConfig
          ).getAccessibleVendors(false, false, consigneeSellerAccountId);
        },
        (error) => {
          trackError('PurchaseClient.getAccessibleVendors', error);
        }
      );
    },

    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    networkMode: 'offlineFirst',
  });

  const availableVendors = useMemo(() => {
    const dataSorted = query.data?.toSorted(
      (a: PurchaseVendor, b: PurchaseVendor) => a.name.localeCompare(b.name)
    );

    const results = dataSorted?.reduce(
      (r, cur) => {
        r[cur.id] = cur;
        return r;
      },
      {} as Record<string, PurchaseVendor>
    );

    return results ?? {};
  }, [query.data]);

  const favoriteVendorNames = useMemo(() => {
    return Object.values(availableVendors ?? {})
      .filter((v) => favoriteVendorIds.includes(v.id))
      .map((v) => v.name);
  }, [favoriteVendorIds, availableVendors]);

  const availableVendorsOptions = useMemo(() => {
    const dataSorted = query.data?.toSorted(
      (a: PurchaseVendor, b: PurchaseVendor) => a.name.localeCompare(b.name)
    );

    const results = dataSorted?.reduce(
      (r, cur) => {
        r[cur.id] = cur.name;
        return r;
      },
      (additionalOptions && additionalOptionsPosition === 'top'
        ? additionalOptions
        : {}) as Record<string, string>
    );

    return {
      ...(additionalOptionsPosition === 'top' ? additionalOptions : {}),
      ...(results ?? {}),
      ...(additionalOptionsPosition === 'bottom' ? additionalOptions : {}),
    };
  }, [query.data, additionalOptions, additionalOptionsPosition]);

  return {
    query,
    availableVendors,
    posSelectProps: {
      loading: query.isLoading,
      placeholderText: placeholderText ?? ContentId.ChooseVendor,
      valueOptionsContent: availableVendorsOptions,
      sortMode: 'ascending',
      searchable: true,
      sortFn: (a: string, b: string) => {
        const addOptionTexts = Object.values(additionalOptions ?? {});
        if (addOptionTexts.includes(a)) {
          return additionalOptionsPosition === 'top' ? -1 : 1;
        }
        if (addOptionTexts.includes(b)) {
          return additionalOptionsPosition === 'top' ? 1 : -1;
        }

        // Comparing by name is fine because vendor names are supposed to be unique
        const aIsFavorite = favoriteVendorNames.includes(a);
        const bIsFavorite = favoriteVendorNames.includes(b);
        if (aIsFavorite && !bIsFavorite) {
          return -1;
        }
        if (!aIsFavorite && bIsFavorite) {
          return 1;
        }

        return a.localeCompare(b);
      },
    },
  };
};
